import { call, put, select } from 'redux-saga/effects';
import * as types from '../../constants/ActionTypes';
import Api from '../../functions/Api';
import {
  sendTicketsData,
  sendTicketCategoriesData,
  deleteTicket,
} from './publishingTicketsSaga';

export { sendTicketsData, sendTicketCategoriesData, deleteTicket };

/**
 * Add, remove or edit the attractions of a product. Adding writes the attraction at its Google place
 * (`v1/attractions/by-place/`) before the product's sights are patched.
 * @param {Object} data - attraction data -  can include `id`, `entrance` and other props
 * @param {String} purpose - which type of API to use (`"add"`, `"edit"` or `"remove"`)
 * @param {Number} idAttraction - attraction id
 */
export function* sendAttractionsData({ data, purpose }) {
  try {
    const { receivedPublishData: product, attractions } = yield select(
      (state) => state.publishing
    );

    let attraction = data;
    if (purpose === 'add') {
      // Only what describes the place: `entrance` from the price tab is the author's admittance for
      // this product, not a fact about a place other tours share (and a protected one refuses it).
      const { google_place_id: googlePlaceId, name, address, location } = data;
      const written = yield call(
        Api.put,
        `v1/attractions/by-place/${encodeURIComponent(googlePlaceId)}/`,
        { data: { name, address, location } }
      );
      // by-place answers with the written fields only: the rest of what the list shows is what the
      // author picked, and a place just added holds no tickets of this product yet.
      attraction = { tickets: [], ...written, ...data };
    }

    const isIncluded = attractions.some(({ id }) => id === attraction.id);

    const getAdmittance = (item) =>
      Boolean(item?.entrance ?? item?.admittance ?? data?.entrance);

    const nextSights = (list) =>
      list
        .filter((x) => x && x.id)
        .map((x) => ({
          attraction_id: x.id,
          admittance: getAdmittance(x),
        }));

    if (
      (purpose === 'add' && attraction.id && !isIncluded) ||
      purpose === 'edit'
    ) {
      const nextAttractions =
        purpose === 'add'
          ? [
              ...attractions,
              { ...attraction, entrance: getAdmittance(attraction) },
            ]
          : attractions.map((a) =>
              a.id === attraction.id
                ? { ...a, ...attraction, entrance: getAdmittance(attraction) }
                : a
            );

      yield put({
        type: types.PATCH_PRODUCT_V1,
        data: {
          sights: nextSights(nextAttractions),
        },
        id: product.id,
      });

      yield put({
        type: types.SEND_ATTRACTIONS_DATA_SUCCESS,
        attractions: nextAttractions,
      });
    }

    if (purpose === 'remove') {
      const nextAttractions = attractions.filter(
        (x) => x && x.id !== attraction.id
      );

      yield put({
        type: types.PATCH_PRODUCT_V1,
        data: {
          sights: nextSights(nextAttractions),
        },
        id: product.id,
      });

      yield put({
        type: types.DELETE_ATTRACTIONS_DATA_SUCCESS,
        id: attraction.id,
      });

      yield put({
        type: types.SEND_ATTRACTIONS_DATA_SUCCESS,
        attractions: nextAttractions,
      });
    }
  } catch (error) {
    yield put({
      type: types.SEND_ATTRACTIONS_DATA_FAILURE,
      errors: [error],
    });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}
